import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isApiError } from '../api/client';
import { filmStocks, isDuplicateStockBody, type FilmStock } from '../api/filmStocks';
import { queryKeys } from '../api/queryKeys';
import { useAdminCall } from '../hooks/useAdminCall';
import { brandsOf, splitNewStockQuery } from '../utils/filmStocks';
import { FilmStockForm } from './FilmStockForm';
import { ModalDialog } from './ModalDialog';

export interface AddFilmStockDialogProps {
  /** What was typed in the picker; the brand is split off if it's known. */
  query: string;
  stocks: FilmStock[];
  /** The stock to use: the one just added, or the one that already had the name. */
  onChosen: (stock: FilmStock) => void;
  onClose: () => void;
}

/**
 * Add a film stock without leaving the page, from a picker's "Add ‘…’ as a
 * new stock" option. Render it only while it's open.
 */
export function AddFilmStockDialog({ query, stocks, onChosen, onClose }: AddFilmStockDialogProps) {
  const call = useAdminCall();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<FilmStock | null>(null);

  const create = useMutation({
    mutationFn: (input: Parameters<typeof filmStocks.create>[0]) => call(() => filmStocks.create(input)),
    onSuccess: (stock) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.filmStocks() });
      onChosen(stock);
    },
    onError: (e) => {
      const body = isApiError(e, 409) ? e.body : null;
      setDuplicate(isDuplicateStockBody(body) ? (stocks.find((s) => s.stock_id === body.stock_id) ?? null) : null);
      setError((isApiError(e) && e.serverMessage) || 'Adding the stock failed. Try again.');
    },
  });

  return (
    <ModalDialog title="Add a film stock" onClose={onClose}>
      <FilmStockForm
        initial={splitNewStockQuery(query, brandsOf(stocks))}
        brands={brandsOf(stocks)}
        saving={create.isPending}
        error={error}
        duplicate={duplicate}
        onSubmit={(input) => create.mutate(input)}
        onCancel={onClose}
        onOpenDuplicate={onChosen}
        duplicateActionLabel={(label) => `Use ${label} instead`}
      />
    </ModalDialog>
  );
}
